/**
 * OTLP INSTRUMENTATION-SCOPE name vocabulary -- the closed set of `scope.name` values this repo's producers stamp on
 * their `scopeSpans` / `scopeLogs` arm.
 *
 * THE CONTRACT, stated because getting it wrong is the failure this leaf exists to prevent: A SCOPE NAMES THE
 * INSTRUMENTATION, NOT THE SERVICE. It answers "which library built these records", so a collector can attribute a row
 * to the producing code path once several producers land in one table. SERVICE identity is a different fact on a
 * different block -- the OTel-standard `service.name` resource key, emitted by `otlpResourceAttributes`
 * (`const/otlp-span-protocol.ts`) off the caller's `OtlpResourceIdentity`. The two are deliberately allowed to differ:
 * the browser leg claims the service `federa-web` while its scope is `WEB_TELEMETRY`, and the hook child claims
 * `claude-code-hooks` while its scope is `HOOKS`. Collapsing either pair would put one fact in two places and lose the
 * other entirely.
 *
 * CLOSED SET, ONE MEMBER PER LIVE EMITTER -- the membership rule is the same one `FEDERA_ATTRS`
 * (`const/telemetry-federa-attrs.ts`) states for wire keys: a member is added in the change that adds the emitter
 * stamping it, never ahead of it, because a scope nobody emits is vocabulary inventory a collector never sees.
 *
 * BEING CLOSED IS WHAT MAKES THE SECOND QUESTION ANSWERABLE. "Is this `federa.`-prefixed scope one we declare ANYWHERE"
 * has no answer while each name sits as a per-emitter module const -- three separate homes, none of which can see the
 * others, so a fourth emitter inventing a second spelling of an existing leg (or a name colliding with one) reads as
 * ordinary until a collector partitions one producer's rows two ways. That is the state this leaf replaced on
 * 2026-08-09, and the paired rule's namespace arm is the question the enumeration now lets it ask.
 *
 * NAMING: `federa.<library>`, the OTel convention of naming a scope after the instrumentation package. The `federa.`
 * segment is the same namespace `FEDERA_ATTR_NAMESPACE` declares for wire keys, and it is deliberately NOT re-derived
 * here -- the paired rule reads that constant directly, so the prefix keeps one home.
 *
 * READ KEY -- every producer here POSTs an OTLP export request and so owns a `scopeLogs` / `scopeSpans` arm, putting
 * the name on `scope.name` where the spec puts it. On the ingest side the normalizer's `scopeIdentity`
 * (`tools/observability/normalize-and-scrub-telemetry-events.ts`) promotes an inbound arm's name onto the
 * `OTEL_ATTRS.SCOPE_NAME` (`otel.scope.name`) attribute -- the OTel collector convention -- so a query asking "which
 * instrumentation produced this row" reads one key whichever leg wrote it. The normalizer layers a record's own
 * attribute OVER the inherited arm, so a record naming itself wins, which is the right precedence.
 *
 * PAIRED FORBID-RULE: `QUAL-OTLP-SCOPE-NAME-RAW-LITERAL-001`, whose alternation is derived from this record at module
 * load, so a new member is governed with no edit to the rule.
 */

export const OTLP_SCOPE_NAMES = {
	/** The BROWSER telemetry leg (`const/browser-telemetry-attrs.ts`): page-lifetime events, Web Vitals and client errors batched from the web app. */
	WEB_TELEMETRY: 'federa.web-telemetry',
	/** The FLEET span leg (`utils/telemetry/otlp-span-build.ts`): one root span per flow run plus its node children. */
	FLOW_ENGINE: 'federa.flow-engine',
	/** The HOOKS leg (`utils/hooks/post-tool-use-failure-telemetry-emit.ts`): the `tool_failure_detail` LogRecord a PostToolUseFailure hook child POSTs to the local collector. */
	HOOKS: 'federa.hooks',
	/**
	 * THE PLATFORM'S OWN SPAN LEG: Cloudflare `workers-observability`, the scope every fleet-bound worker's run span
	 * arrives under. Landed 2026-08-29 with the first feed that READS it -- the search-miss event metric, whose
	 * producer re-seated off the bearer door onto its own run span.
	 *
	 * IT IS THE ONE MEMBER THIS REPO DOES NOT EMIT, and that is the fact worth stating rather than smoothing over.
	 * Every sibling above names an emitter module in this tree; this one names the RUNTIME. A worker whose manifest
	 * carries `observability: 'fleet'` has its spans exported by the platform with no application credential and no
	 * code of ours on the path, so there is no leaf to cite and no way to change what the scope is called. It earns a
	 * member anyway because a reader must be able to say WHO produces a set of rows, and "the platform" is a true and
	 * probeable answer where a free-text string would not be.
	 *
	 * DO NOT READ IT AS A LEG OF OURS. It is not a vocabulary this tree chose and it carries no `federa.` prefix
	 * precisely because it is not ours -- the bare spelling is the wire value Cloudflare stamps, verified against a
	 * live `storage-gateway-dev` row on 2026-08-29.
	 */
	PLATFORM_WORKERS_OBSERVABILITY: 'workers-observability',
} as const;

/** One producing surface's scope name -- the type-position companion to {@link OTLP_SCOPE_NAMES}, on the same
 *  const-plus-derived-type shape `TelemetrySignal` takes beside `TELEMETRY_SIGNALS` (`const/telemetry.ts`).
 *
 *  IT EXISTS BECAUSE THE SET IS A TYPE POSITION AND NOT ONLY A VALUE ONE: a reader that resolves a caller's string
 *  against these members before it will read anything needs a name for "a member of this record", and two
 *  hand-written `(typeof OTLP_SCOPE_NAMES)[keyof typeof OTLP_SCOPE_NAMES]` spellings would be the same derivation
 *  authored twice, free to be narrowed on one side only. */
export type OtlpScopeName = (typeof OTLP_SCOPE_NAMES)[keyof typeof OTLP_SCOPE_NAMES];
